/**
 * Fades and slides content blocks into place the first time they scroll into view.
 *
 * Progressive enhancement: blocks are only hidden by this script, and only while
 * they are below the viewport, so content stays visible when the module is slow,
 * blocked, or the visitor prefers reduced motion. Styles live in base.css.
 */

const TARGET_SELECTOR =
  '.text-block, .image-block, .icon-block__media, .button, .button-secondary, .button-custom, product-card, .variant-card, .variant-list__heading, .email-signup-block';

/** Containers whose contents open on demand or carry form state; leave those alone. */
const EXCLUDED_ANCESTOR_SELECTOR = 'form, dialog, [popover], details, header';

const SCOPE_SELECTOR = '#MainContent, footer';

const STAGGER_MS = 90;
const MAX_STAGGER_STEPS = 5;
/** Longest reveal (duration plus the largest stagger delay) with a little slack. */
const CLEANUP_MS = 1400;

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** @type {IntersectionObserver | null} */
let observer = null;

/**
 * @param {Element} element
 * @returns {boolean}
 */
function isRevealTarget(element) {
  const parent = element.parentElement;
  if (!parent) return false;
  if (parent.closest(EXCLUDED_ANCESTOR_SELECTOR)) return false;
  // Reveal the outermost match only, so nested blocks do not animate twice.
  return !parent.closest(TARGET_SELECTOR);
}

/**
 * @param {IntersectionObserverEntry[]} entries
 */
function handleIntersect(entries) {
  let step = 0;

  for (const entry of entries) {
    if (!entry.isIntersecting) continue;

    const element = entry.target;
    observer?.unobserve(element);
    if (!(element instanceof HTMLElement)) continue;

    element.style.setProperty('--reveal-delay', `${Math.min(step, MAX_STAGGER_STEPS) * STAGGER_MS}ms`);
    element.dataset.reveal = 'in';
    step += 1;

    // Hand the element back to its own transitions (hover states and so on) once it has settled.
    window.setTimeout(() => {
      delete element.dataset.reveal;
      element.style.removeProperty('--reveal-delay');
    }, CLEANUP_MS);
  }
}

/**
 * Hides targets that are still below the viewport and watches for them to scroll in.
 * @param {ParentNode} root
 */
function prepare(root) {
  if (!observer) return;

  const viewportHeight = window.innerHeight;

  for (const element of root.querySelectorAll(TARGET_SELECTOR)) {
    if (!(element instanceof HTMLElement)) continue;
    if (element.dataset.reveal) continue;
    if (!isRevealTarget(element)) continue;

    // Anything already on screen (or above it) stays put so nothing blinks out on load.
    if (element.getBoundingClientRect().top < viewportHeight) continue;

    element.dataset.reveal = 'pending';
    observer.observe(element);
  }
}

function init() {
  const main = document.getElementById('MainContent');
  if (main?.dataset.scrollReveal !== 'true') return;
  if (reducedMotion.matches || !('IntersectionObserver' in window)) return;

  observer = new IntersectionObserver(handleIntersect, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

  for (const scope of document.querySelectorAll(SCOPE_SELECTOR)) {
    prepare(scope);
  }

  // Sections added or re-rendered in the theme editor get the same treatment.
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target instanceof Element && event.target.closest(SCOPE_SELECTOR)) prepare(event.target);
  });

  // If the visitor turns reduced motion on mid-visit, show everything straight away.
  reducedMotion.addEventListener('change', (event) => {
    if (!event.matches) return;
    observer?.disconnect();
    for (const element of document.querySelectorAll('[data-reveal]')) {
      if (element instanceof HTMLElement) delete element.dataset.reveal;
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
